import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './Herosection.css';
import { FaHeartbeat, FaBrain, FaBaby, FaBone, FaAmbulance, FaCalendarCheck } from 'react-icons/fa';
import { MdOutlineVerified, MdHealthAndSafety } from 'react-icons/md';
import { GiMedicalPack } from 'react-icons/gi';

const slides = [
    {
        id: 0,
        badge: 'Advanced Cardiac Care',
        badgeIcon: <FaHeartbeat />,
        headline: 'Your Heart Deserves',
        headlineAccent: 'The Best Care.',
        sub: 'JDS Hospitals offers world-class cardiology services with cutting-edge diagnostics and compassionate specialists dedicated to your heart health.',
        accent: 'from #ef4444 to #f97316',
        accentFrom: '#ef4444',
        accentTo: '#f97316',
        blobColor1: '#fecaca',
        blobColor2: '#fed7aa',
        stats: [
            { val: '25+', lbl: 'Years of Care' },
            { val: '10K+', lbl: 'Heart Patients' },
            { val: '24/7', lbl: 'Emergency' },
        ],
        services: [
            { icon: <FaHeartbeat />, label: 'Cardiology', color: '#ef4444' },
            { icon: <FaBrain />, label: 'Neurology', color: '#8b5cf6' },
            { icon: <FaBaby />, label: 'Pediatrics', color: '#06b6d4' },
            { icon: <FaBone />, label: 'Orthopedics', color: '#f59e0b' },
            { icon: <MdHealthAndSafety />, label: 'General Medicine', color: '#10b981' },
            { icon: <GiMedicalPack />, label: 'Emergency', color: '#ef4444' },
        ]
    },
    {
        id: 1,
        badge: 'Expert Neurology',
        badgeIcon: <FaBrain />,
        headline: 'Healing Minds,',
        headlineAccent: 'Restoring Lives.',
        sub: 'Our neurology department combines the latest medical research with compassionate hands-on care to treat complex brain and nervous system conditions.',
        accentFrom: '#7c3aed',
        accentTo: '#4f46e5',
        blobColor1: '#ede9fe',
        blobColor2: '#c7d2fe',
        stats: [
            { val: '150+', lbl: 'Specialists' },
            { val: '50K+', lbl: 'Patients Served' },
            { val: '24/7', lbl: 'ICU Support' },
        ],
        services: [
            { icon: <FaBrain />, label: 'Neurology', color: '#8b5cf6' },
            { icon: <FaHeartbeat />, label: 'Cardiology', color: '#ef4444' },
            { icon: <FaBaby />, label: 'Neonatology', color: '#06b6d4' },
            { icon: <FaBone />, label: 'Spine Care', color: '#f59e0b' },
            { icon: <FaAmbulance />, label: 'Emergency', color: '#ef4444' },
            { icon: <MdHealthAndSafety />, label: 'Rehabilitation', color: '#10b981' },
        ]
    },
    {
        id: 2,
        badge: '24/7 Emergency Care',
        badgeIcon: <FaAmbulance />,
        headline: 'Always Here,',
        headlineAccent: 'Every Moment.',
        sub: 'With round-the-clock emergency services and a dedicated rapid-response team, JDS Hospitals ensures you are never alone in a medical crisis.',
        accentFrom: '#0284c7',
        accentTo: '#0d9488',
        blobColor1: '#bae6fd',
        blobColor2: '#99f6e4',
        stats: [
            { val: '<5 min', lbl: 'Response Time' },
            { val: '100+', lbl: 'ICU Beds' },
            { val: '24/7', lbl: 'Trauma Unit' },
        ],
        services: [
            { icon: <FaAmbulance />, label: 'Emergency', color: '#ef4444' },
            { icon: <GiMedicalPack />, label: 'Trauma Care', color: '#f59e0b' },
            { icon: <FaHeartbeat />, label: 'Cardiac ICU', color: '#ef4444' },
            { icon: <FaBrain />, label: 'Neuro ICU', color: '#8b5cf6' },
            { icon: <FaBaby />, label: 'NICU', color: '#06b6d4' },
            { icon: <MdHealthAndSafety />, label: 'Critical Care', color: '#10b981' },
        ]
    },
    {
        id: 3,
        badge: 'Book an Appointment',
        badgeIcon: <FaCalendarCheck />,
        headline: 'Compassionate Care,',
        headlineAccent: 'Personalised for You.',
        sub: 'From routine check-ups to complex surgeries, JDS Hospitals has served Visakhapatnam with trust, excellence, and patient-first values since 2008.',
        accentFrom: '#0284c7',
        accentTo: '#7c3aed',
        blobColor1: '#bae6fd',
        blobColor2: '#ede9fe',
        stats: [
            { val: '2008', lbl: 'Established' },
            { val: '50+', lbl: 'Specialties' },
            { val: '25+', lbl: 'Years of Trust' },
        ],
        services: [
            { icon: <MdOutlineVerified />, label: 'NABH Certified', color: '#0284c7' },
            { icon: <FaHeartbeat />, label: 'Cardiology', color: '#ef4444' },
            { icon: <FaBrain />, label: 'Neurology', color: '#8b5cf6' },
            { icon: <FaBaby />, label: 'Pediatrics', color: '#06b6d4' },
            { icon: <FaBone />, label: 'Orthopedics', color: '#f59e0b' },
            { icon: <FaCalendarCheck />, label: 'Book Now', color: '#10b981' },
        ]
    }
];

const slideVariants = {
    enter: (dir) => ({ opacity: 0, x: dir > 0 ? 80 : -80 }),
    center: { opacity: 1, x: 0, transition: { duration: 0.55, ease: [0.2, 0.65, 0.3, 0.9] } },
    exit: (dir) => ({ opacity: 0, x: dir > 0 ? -80 : 80, transition: { duration: 0.4, ease: "easeIn" } })
};

function Herosection() {
    const [current, setCurrent] = useState(0);
    const [dir, setDir] = useState(1);

    useEffect(() => {
        const timer = setInterval(() => {
            setDir(1);
            setCurrent(prev => (prev + 1) % slides.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    const goTo = (idx) => {
        setDir(idx > current ? 1 : -1);
        setCurrent(idx);
    };

    const slide = slides[current];

    return (
        <section className="hero-section" id='Home'>
            {/* Animated background blobs */}
            <div className="hero-bg">
                <motion.div
                    className="hero-blob b1"
                    animate={{ background: `radial-gradient(circle, ${slide.blobColor1} 0%, transparent 70%)` }}
                    transition={{ duration: 1.2 }}
                />
                <motion.div
                    className="hero-blob b2"
                    animate={{ background: `radial-gradient(circle, ${slide.blobColor2} 0%, transparent 70%)` }}
                    transition={{ duration: 1.2 }}
                />
                <div className="hero-grid-dots"></div>
                <div className="hero-noise-tex"></div>
            </div>

            <div className="hero-wrap">
                <AnimatePresence custom={dir} mode="wait">
                    <motion.div
                        key={current}
                        className="hero-slide"
                        custom={dir}
                        variants={slideVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                    >
                        {/* Badge */}
                        <div className="hero-badge">
                            <span className="badge-ico" style={{ color: slide.accentFrom }}>{slide.badgeIcon}</span>
                            <span>{slide.badge}</span>
                        </div>

                        {/* Headline */}
                        <h1 className="hero-h1">
                            {slide.headline}<br />
                            <span className="hero-gradient-text" style={{
                                backgroundImage: `linear-gradient(135deg, ${slide.accentFrom} 0%, ${slide.accentTo} 100%)`
                            }}>
                                {slide.headlineAccent}
                            </span>
                        </h1>

                        {/* Description */}
                        <p className="hero-sub">{slide.sub}</p>

                        {/* CTA */}
                        <div className="hero-ctas">
                            <button className="hero-btn-primary" style={{
                                background: `linear-gradient(135deg, ${slide.accentFrom} 0%, ${slide.accentTo} 100%)`
                            }}>
                                Book Appointment
                            </button>
                            <button className="hero-btn-outline">Our Services</button>
                        </div>

                        {/* Stats */}
                        <div className="hero-stats">
                            {slide.stats.map((s, i) => (
                                <React.Fragment key={i}>
                                    <div className="hero-stat">
                                        <span className="stat-v" style={{ color: slide.accentFrom }}>{s.val}</span>
                                        <span className="stat-l">{s.lbl}</span>
                                    </div>
                                    {i < slide.stats.length - 1 && <div className="stat-sep" />}
                                </React.Fragment>
                            ))}
                        </div>

                        {/* Service Cards */}
                        <div className="hero-service-grid">
                            {slide.services.map((s, i) => (
                                <motion.div
                                    className="svc-card"
                                    key={i}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.3 + i * 0.07 }}
                                    whileHover={{ y: -5, scale: 1.05 }}
                                >
                                    <span className="svc-icon" style={{ color: s.color, background: `${s.color}18` }}>{s.icon}</span>
                                    <span className="svc-lbl">{s.label}</span>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </AnimatePresence>

                {/* Dot Navigation */}
                <div className="hero-dots">
                    {slides.map((_, i) => (
                        <button
                            key={i}
                            className={`hero-dot ${i === current ? 'active' : ''}`}
                            onClick={() => goTo(i)}
                            style={i === current ? { background: slide.accentFrom } : {}}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
}

export default Herosection;
